import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { RunAdvancedConfig } from "@/pages/tina/runs/components/run-advanced-config";
import { RunPresetsBar } from "@/pages/tina/runs/components/run-presets-bar";
import { RunSourcePicker } from "@/pages/tina/runs/components/run-source-picker";
import {
  CONTENT_TYPES,
  CONTENT_TYPE_LABEL,
  type ContentType,
  type RunPreset,
  SOURCE_KINDS,
  SOURCE_LABEL,
  type SourceKind,
  type TinaSiteOption,
  comboKey,
  createPresetId,
  loadPresets,
  persistPresets,
} from "@/pages/tina/runs/components/site-picker/utils";
import { useRunPickerSites } from "@/pages/tina/runs/hooks/use-run-picker-sites";
import { useRunsRepository } from "@/pages/tina/runs/hooks/use-runs-repository";
import {
  type AdvancedConfigType,
  advancedConfigSchema,
} from "@/pages/tina/runs/schemas/run.create.form";
import {
  type CityFacet,
  useSiteFacets,
} from "@/pages/tina/sites/hooks/use-site-facets";
import type { RunConfig } from "@/types/tina";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export const RunsCreate = () => {
  const repo = useRunsRepository();

  const [contentType, setContentType] = useState<ContentType>("music_event");
  const [source, setSource] = useState<SourceKind>("websites");
  const [country, setCountry] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [presets, setPresets] = useState<RunPreset[]>(() => loadPresets());
  const [knownSites, setKnownSites] = useState<Map<string, TinaSiteOption>>(
    () => new Map(),
  );

  const form = useForm<AdvancedConfigType>({
    resolver: zodResolver(advancedConfigSchema),
    defaultValues: { dry_run: false, prompt_overrides: [] },
  });

  // The picker's "websites" source maps to the backend `source_kind=web`;
  // "instagram"/"facebook" pass through as their own source_kind groups.
  const sourceKind =
    source === "instagram" ? "instagram" : source === "facebook" ? "facebook" : "web";

  // Country + city options come from /sites/facets (no counts), scoped to enabled
  // sites in the active content type / source. The city list holds every city
  // with its country, so a city can be searched without picking a country.
  const facets = useSiteFacets({
    contentType,
    sourceKind,
    enabled: true,
  });
  const countryOptions = facets.countries;
  const cityOptions = facets.cityCountries;

  // Debounce the search input so we don't refetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedQuery(query),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [query]);

  const { rows, total, isLoading, error, fetchAllInScope } = useRunPickerSites({
    contentType,
    sourceKind,
    countryCode: country,
    city,
    query: debouncedQuery,
    page,
    pageSize: PAGE_SIZE,
  });

  // Accumulate the records we've seen (pages + select-all) so the basket can
  // show a city for any selected site, even ones not on the current page.
  useEffect(() => {
    if (rows.length === 0) return;
    setKnownSites((prev) => {
      const next = new Map(prev);
      for (const site of rows) next.set(site.name, site);
      return next;
    });
  }, [rows]);

  // Keep the selected country valid for the active content × source.
  useEffect(() => {
    if (countryOptions.length === 0) {
      setCountry((current) => (current === null ? current : null));
      return;
    }
    setCountry((current) =>
      current && countryOptions.includes(current) ? current : countryOptions[0],
    );
  }, [countryOptions]);

  // City + search are refinements of the current scope — reset on scope change.
  // A country change is handled in `changeCountry`/`pickCity`, not here, so
  // picking a city can set its country without wiping that city.
  useEffect(() => {
    setCity(null);
    setQuery("");
  }, [contentType, source]);

  const changeCountry = useCallback((next: string) => {
    setCountry(next);
    setCity(null);
    setQuery("");
  }, []);

  const pickCity = useCallback((pick: CityFacet | null) => {
    setQuery("");
    if (pick === null) {
      setCity(null);
      return;
    }
    setCountry(pick.country_code);
    setCity(pick.city);
  }, []);

  // Any scope/search change returns to the first page.
  useEffect(() => {
    setPage(1);
  }, [contentType, source, country, city, debouncedQuery]);

  const key = comboKey(contentType, source, country);
  const selectedNames = useMemo(() => selections[key] ?? [], [selections, key]);
  const selectedSet = useMemo(() => new Set(selectedNames), [selectedNames]);

  const setSelectedNames = useCallback(
    (names: string[]) => {
      setSelections((prev) => ({ ...prev, [key]: Array.from(new Set(names)) }));
    },
    [key],
  );

  const onToggle = useCallback(
    (name: string) =>
      setSelectedNames(
        selectedSet.has(name)
          ? selectedNames.filter((current) => current !== name)
          : [...selectedNames, name],
      ),
    [selectedNames, selectedSet, setSelectedNames],
  );
  const onSelect = useCallback(
    (names: string[]) => setSelectedNames([...selectedNames, ...names]),
    [selectedNames, setSelectedNames],
  );
  const onDeselect = useCallback(
    (names: string[]) => {
      const remove = new Set(names);
      setSelectedNames(selectedNames.filter((name) => !remove.has(name)));
    },
    [selectedNames, setSelectedNames],
  );
  const onClear = useCallback(() => setSelectedNames([]), [setSelectedNames]);

  // "Select all in country" fetches every name in the current content × source ×
  // country scope (server-side, paged) and selects them.
  const onSelectAllInScope = useCallback(async () => {
    const all = await fetchAllInScope();
    if (all.length === 0) return;
    setKnownSites((prev) => {
      const next = new Map(prev);
      for (const site of all) next.set(site.name, site);
      return next;
    });
    onSelect(all.map((site) => site.name));
  }, [fetchAllInScope, onSelect]);

  const contentPresets = useMemo(
    () => presets.filter((preset) => preset.contentType === contentType),
    [presets, contentType],
  );

  const savePreset = useCallback(
    (name: string) => {
      if (!country || selectedNames.length === 0) return;
      const preset: RunPreset = {
        id: createPresetId(),
        name,
        contentType,
        source,
        country,
        sites: selectedNames,
        createdAt: new Date().toISOString(),
      };
      const next = [
        preset,
        ...presets.filter(
          (existing) =>
            !(existing.contentType === contentType && existing.name === name),
        ),
      ];
      setPresets(next);
      persistPresets(next);
    },
    [contentType, country, presets, selectedNames, source],
  );

  const loadPreset = useCallback((preset: RunPreset) => {
    setSource(preset.source);
    setCountry(preset.country);
    setCity(null);
    setQuery("");
    setSelections((prev) => ({
      ...prev,
      [comboKey(preset.contentType, preset.source, preset.country)]: Array.from(
        new Set(preset.sites),
      ),
    }));
  }, []);

  const deletePreset = useCallback(
    (id: string) => {
      const next = presets.filter((preset) => preset.id !== id);
      setPresets(next);
      persistPresets(next);
    },
    [presets],
  );

  const onTrigger = form.handleSubmit(async (values) => {
    if (selectedNames.length === 0) return;
    const config: RunConfig = {};
    if (values.score_threshold !== undefined)
      config.score_threshold = values.score_threshold;
    if (values.cost_cap_usd !== undefined)
      config.cost_cap_usd = String(values.cost_cap_usd);
    if (values.dry_run) config.dry_run = values.dry_run;
    if (values.prompt_overrides && values.prompt_overrides.length > 0) {
      config.prompt_overrides = Object.fromEntries(
        values.prompt_overrides.map((row) => [
          row.stage,
          { prompt_version_id: row.prompt_version_id },
        ]),
      );
    }

    await repo.trigger({
      sites: selectedNames,
      limit: values.limit,
      config: Object.keys(config).length > 0 ? config : undefined,
    });
  });

  const scopeLabel = `${CONTENT_TYPE_LABEL[contentType]} · ${SOURCE_LABEL[source]}${
    country ? ` · ${country}` : ""
  }`;
  const canTrigger = selectedNames.length > 0 && !form.formState.isSubmitting;

  return (
    <div className="flex w-full max-w-none flex-col gap-6 p-6 pb-24">
      <div>
        <h1 className="text-2xl font-semibold">Trigger run</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a content type and source, choose a country or search a city, then
          select the sites to seed.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={contentType}
          onValueChange={(value) => setContentType(value as ContentType)}
        >
          <TabsList>
            {CONTENT_TYPES.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="inline-flex overflow-hidden rounded-md border">
          {SOURCE_KINDS.map((option, index) => (
            <Button
              key={option.value}
              type="button"
              variant={source === option.value ? "secondary" : "ghost"}
              size="sm"
              className={cn(
                "rounded-none",
                index > 0 && "border-l",
                source === option.value && "font-semibold",
              )}
              onClick={() => setSource(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <RunSourcePicker
        rows={rows}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        isLoading={isLoading}
        error={error}
        contentType={contentType}
        source={source}
        country={country}
        countryOptions={countryOptions}
        onCountryChange={changeCountry}
        city={city}
        onCityChange={pickCity}
        cityOptions={cityOptions}
        query={query}
        onQueryChange={setQuery}
        selectedNames={selectedNames}
        selected={selectedSet}
        knownSites={knownSites}
        onToggle={onToggle}
        onSelect={onSelect}
        onSelectAllInScope={onSelectAllInScope}
        onDeselect={onDeselect}
        onClear={onClear}
      />

      <RunPresetsBar
        presets={contentPresets}
        canSave={Boolean(country) && selectedNames.length > 0}
        onSave={savePreset}
        onLoad={loadPreset}
        onDelete={deletePreset}
      />

      <RunAdvancedConfig form={form} />

      <BottomActionBar className="items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            {selectedNames.length} site{selectedNames.length === 1 ? "" : "s"}
          </span>{" "}
          · {scopeLabel}
        </span>
        <Button type="button" disabled={!canTrigger} onClick={onTrigger}>
          Trigger {scopeLabel} run
          {selectedNames.length > 0 ? ` (${selectedNames.length})` : ""}
        </Button>
      </BottomActionBar>
    </div>
  );
};
